import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Upload, Tags, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useCreateTagset, useUploadTagFile, useParseTagset } from '@/hooks/useTagsets'
import { useToast } from '@/hooks/use-toast'

export function CreateTagset() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const createTagset = useCreateTagset()
  const uploadFile = useUploadTagFile()
  const parseTagset = useParseTagset()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) {
      const validExtensions = ['.txt', '.csv', '.xlsx']
      const ext = selected.name.substring(selected.name.lastIndexOf('.')).toLowerCase()
      if (!validExtensions.includes(ext)) {
        toast({
          title: 'Invalid file type',
          description: 'Only .txt, .csv, and .xlsx files are supported.',
          variant: 'destructive',
        })
        return
      }
      setFile(selected)
    }
  }

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }

    setIsCreating(true)
    try {
      // Step 1: Create the tagset
      const sourceType = file ? 'upload' : 'empty'
      const result = await createTagset.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        source_type: sourceType,
      })

      const tagsetId = result.tagset.id

      // Step 2: Upload file if provided
      if (file) {
        await uploadFile.mutateAsync({ tagsetId, file })

        // Step 3: Auto-parse after upload
        await parseTagset.mutateAsync(tagsetId)

        toast({
          title: 'Tagset created',
          description: `'${name}' created and is now parsing.`,
        })
      } else {
        toast({
          title: 'Tagset created',
          description: `'${name}' created. Upload a tag file to get started.`,
        })
      }

      navigate(`/app/tagsets/${tagsetId}`)
    } catch (err: any) {
      toast({
        title: 'Failed to create tagset',
        description: err?.response?.data?.error || err.message,
        variant: 'destructive',
      })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => navigate('/app/tagsets')}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Tagsets
      </Button>

      <div className="mb-8">
        <h1 className="text-3xl font-bold">Create Tagset</h1>
        <p className="text-muted-foreground mt-2">
          Create a new tag dataset to enhance retrieval across your knowledge bases
        </p>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Basic Info</CardTitle>
            <CardDescription>
              Give your tagset a descriptive name
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Product Categories, Golf Swing Tags"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description of what these tags represent"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Tag File
            </CardTitle>
            <CardDescription>
              Upload a CSV file with Description and Tag columns. You can also skip this and upload later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              {file ? (
                <div className="space-y-2">
                  <Tags className="h-8 w-8 mx-auto text-primary" />
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFile(null)}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drag & drop or click to select a .csv, .txt, or .xlsx file
                  </p>
                  <Input
                    type="file"
                    accept=".csv,.txt,.xlsx"
                    onChange={handleFileChange}
                    className="max-w-xs mx-auto"
                  />
                </div>
              )}
            </div>
            <div className="mt-4 p-3 bg-muted/50 rounded-md">
              <p className="text-sm font-medium mb-1">Expected Format:</p>
              <code className="text-xs block bg-background p-2 rounded">
                Description,Tag{'\n'}
                "Content about product features","product, features"{'\n'}
                "Customer support inquiries","support, help"
              </code>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/app/tagsets')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isCreating || !name.trim()}
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Tags className="h-4 w-4 mr-2" />
                {file ? 'Create & Parse' : 'Create Tagset'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
